import React, { useContext, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  Divider,
  Grid,
  Typography,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ListResponsive from "../../../../components/other/ListResponsive";
import {
  AdepticonTeamRow,
  DoublesRow,
  PlayerRow,
  SeatedRow,
  TeamRow
} from "./EventPairingRows";
import { AuthContext } from "../../../../components/user/BCPAuthProvider";

export default function EventPairingsOverview(props) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventPairings"
  });

  const {
    event,
    pairings,
    roundNo,
    organize,
    open,
    setOpen,
    message1,
    message2,
    color,
    animate
  } = props;
  const pauseTime = 3000; // Pause time before scrolling starts

  const authContext = useContext(AuthContext);
  const theme = useTheme();
  const containerRef = useRef(null);

  const [scrollStart, setScrollStart] = useState(false);

  useLayoutEffect(() => {
    if (!open) return;

    // Delay the start of scrolling by the pauseTime duration
    const delayTimer = setTimeout(() => {
      setScrollStart(true);
    }, pauseTime);

    return () => clearTimeout(delayTimer);
  }, [open]);

  useLayoutEffect(() => {
    if (!scrollStart) return;

    const container = containerRef.current;
    if (!container) return;
    // Adjust the speed of scrolling
    const scrollSpeed = 3;

    let scrollDirection = "down";

    const scroll = () => {
      if (scrollDirection === "down") {
        container.scrollTop += scrollSpeed;
        if (
          container.scrollTop + container.clientHeight >=
          container.scrollHeight
        ) {
          scrollDirection = "pauseDown";
          setTimeout(() => (scrollDirection = "up"), pauseTime);
        }
      } else if (scrollDirection === "up") {
        container.scrollTop -= scrollSpeed;
        if (container.scrollTop <= 0) {
          scrollDirection = "pauseUp";
          setTimeout(() => (scrollDirection = "down"), pauseTime);
        }
      }
    };

    const interval = setInterval(scroll, 50);

    return () => clearInterval(interval);
  }, [scrollStart]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={() => setOpen(false)}
      sx={{
        "& .MuiPaper-root": {
          alignItems: "center",
          textAlign: "center"
        }
      }}
      onClick={() => setOpen(false)}
    >
      <Box width={"100%"} zIndex={99}>
        <Typography
          variant="h1"
          sx={{ fontSize: ["10vw", "6vw", "4vw", "3vw"] }}
        >
          {event?.name}
        </Typography>
        <Divider />
      </Box>
      <Grid
        container
        spacing={1}
        sx={{
          pt: 1
        }}
      >
        <Grid
          item
          xs={12}
          md={6}
          ref={containerRef}
          sx={{
            maxHeight: "93vh",
            overflowY: "scroll",
            scrollbarColor:
              theme?.palette?.mode === "dark" ? "#6b6b6b #2b2b2b" : "",
            "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
              backgroundColor: theme?.palette?.mode === "dark" ? "#2b2b2b" : ""
            }
          }}
        >
          <Box
            sx={{ position: "absolute", zIndex: 999, top: 0, left: 0 }}
          ></Box>
          <ListResponsive
            originalList={pairings}
            userRowsPerPage="pairings"
            rowProps={{
              headings: (
                <Grid item xs={12}>
                  <Typography
                    variant="h4"
                    color="text.secondary"
                    gutterBottom
                    textAlign="left"
                    pl={1}
                  >
                    Table
                  </Typography>
                </Grid>
              ),
              mapFunction: (row, index) => {
                if (!organize && event?.adepticonTeamEvent) {
                  return (
                    <AdepticonTeamRow
                      row={row}
                      event={event}
                      authContext={authContext}
                    />
                  );
                } else if (row?.pairingType === "SeatedPairing") {
                  return <SeatedRow row={row} />;
                } else if (event?.format?.doublesEvent) {
                  return <DoublesRow row={row} event={event} />;
                } else if (event?.format?.teamEvent) {
                  return <TeamRow row={row} event={event} />;
                } else {
                  return <PlayerRow row={row} event={event} key={index} />;
                }
              }
            }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column"
          }}
        >
          {(roundNo >= 1 || !event.status?.started) && (
            <Typography
              variant="h1"
              sx={{
                color: color,
                fontSize:
                  message2?.trim() === t("noTimeRemaining") ? "3vw" : "4vw",
                "@keyframes blinker": {
                  "50%": {
                    opacity: 0
                  }
                }
              }}
              gutterBottom
            >
              {t("round")} {roundNo}
            </Typography>
          )}

          <Typography
            variant="h1"
            sx={{
              color: color,
              animation: animate ? "blinker 1s linear infinite" : "",
              fontSize: message1?.trim() === t("eventBeginsIn") ? "6vw" : "8vw",
              "@keyframes blinker": {
                "50%": {
                  opacity: 0
                }
              }
            }}
            gutterBottom
          >
            {message1}
          </Typography>
          <Typography
            variant="h1"
            sx={{
              color: color,
              animation: animate ? "blinker 1s linear infinite" : "",
              fontSize:
                message2?.trim() === t("noTimeRemaining") ? "3vw" : "4vw",
              "@keyframes blinker": {
                "50%": {
                  opacity: 0
                }
              }
            }}
          >
            {message2?.trim()}
          </Typography>
        </Grid>
      </Grid>
    </Dialog>
  );
}
